import { Box, SelectChangeEvent } from '@mui/material';
import { SyntheticEvent, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import CharacterLimitHelperText from '@/components/common/CharacterLimitHelperText';
import { SelectInput } from '@/components/common/select_input/SelectInput';
import { ConfigContext } from '@/Contexts/ConfigContext';
import { LocaleContext } from '@/Contexts/LocaleContext';
import { PartnerIntakeContext } from '@/Contexts/PartnerIntakeContext';
import { PartnerSubCategoryProps } from '@/models/PartnerSubCategory';

import RowWrap from './IntakeRowWrap';
import SignUpNames from '../../SignUpNames';

function PartnerCategoryInput() {
  const { partnerSubCategories } = useContext(ConfigContext);
  const { partnerInfo, showErrors, intakeLabels, selectedProgramInfo, updatePartnerInfo } =
    useContext(PartnerIntakeContext);
  const { translatedText } = useContext(LocaleContext);

  const [partnerSubCategoryId, setPartnerSubCategoryId] = useState<number | null>(null);

  const selectedPartnerSubCategory = useMemo(() => {
    return partnerSubCategories.find((c) => c.id == partnerSubCategoryId);
  }, [partnerSubCategoryId, partnerSubCategories]);

  const isOtherCategory = useMemo(() => {
    return (
      selectedPartnerSubCategory?.subCategory.toLowerCase() ==
      translatedText.partner.other.toLowerCase()
    );
  }, [selectedPartnerSubCategory, translatedText.partner.other]);

  useEffect(() => {
    if (partnerInfo.partnerSubCategoryId != null && partnerInfo.partnerSubCategoryId !== 0) {
      setPartnerSubCategoryId(partnerInfo.partnerSubCategoryId);
    }
  }, [partnerInfo.partnerSubCategoryId]);

  // Derived (not stored) so the options follow the selected language: selectedProgramInfo's
  // sub-category names are re-mapped whenever the locale changes.
  const partnerSubCategoryOptions = useMemo<PartnerSubCategoryProps[]>(() => {
    if (
      selectedProgramInfo?.partnerSubCategories == null ||
      selectedProgramInfo.partnerSubCategories.length === 0 ||
      partnerInfo == null ||
      (partnerInfo.partnerSubCategoryId != null && selectedPartnerSubCategory == null)
    )
      return [];

    const otherLabel = translatedText.partner.other.toLocaleLowerCase();

    const subCategories = selectedProgramInfo.partnerSubCategories.filter(
      (c) => c.subCategory.toLocaleLowerCase() != otherLabel
    );

    subCategories.sort((a: PartnerSubCategoryProps, b: PartnerSubCategoryProps) => {
      if ((a.subCategory ?? '') < (b.subCategory ?? '')) {
        return -1;
      } else if ((a.subCategory ?? '') > (b.subCategory ?? '')) {
        return 1;
      }

      return 0; // Add default return value
    });

    if (
      selectedPartnerSubCategory != null &&
      selectedProgramInfo.partnerSubCategories.findIndex(
        (c) => c.id == selectedPartnerSubCategory.id
      ) === -1
    ) {
      subCategories.push(selectedPartnerSubCategory);
    }

    // other is always last option
    const otherCategory = selectedProgramInfo.partnerSubCategories.find(
      (c) => c.subCategory.toLocaleLowerCase() == otherLabel
    );

    if (otherCategory != null) {
      subCategories.push({
        id: otherCategory.id,
        subCategory: otherCategory.subCategory,
      });
    }

    return subCategories;
  }, [
    partnerInfo,
    selectedPartnerSubCategory,
    selectedProgramInfo?.partnerSubCategories,
    translatedText.partner.other,
  ]);

  const handleChange = useCallback(
    (event: SelectChangeEvent) => {
      event.preventDefault();
      event.stopPropagation();

      const updatedValue = Number(event.target.value);
      setPartnerSubCategoryId(updatedValue);

      if (updatePartnerInfo != null && partnerInfo != null) {
        updatePartnerInfo({
          ...partnerInfo,
          partnerSubCategoryId: updatedValue,
        });
      }
    },
    [updatePartnerInfo, partnerInfo]
  );

  const handleOtherCategoryChange = useCallback(
    (event: SyntheticEvent) => {
      const updatedCategoryOther = (event.target as HTMLInputElement).value;

      if (updatePartnerInfo != null && partnerInfo != null) {
        updatePartnerInfo({
          ...partnerInfo,
          partnerSubCategoryOther: updatedCategoryOther,
        });
      }
    },
    [updatePartnerInfo, partnerInfo]
  );

  const otherText = partnerInfo.partnerSubCategoryOther ?? '';

  return (
    <RowWrap>
      <Box flexGrow={1}>
        <SelectInput
          id={'category-select'}
          label={intakeLabels?.partnerSubCategoryId ?? ''}
          required
          value={
            partnerSubCategoryId != null && partnerSubCategoryId !== 0
              ? partnerSubCategoryId.toString()
              : ''
          }
          hasError={showErrors && partnerSubCategoryId == null}
          selectOptions={partnerSubCategoryOptions
            .filter((category) => (category.subCategory ?? '') !== '' && (category.id ?? 0) !== 0)
            .map((category: PartnerSubCategoryProps) => {
              return {
                value: category.id,
                label: category.subCategory,
              };
            })}
          onChange={handleChange}
        />
      </Box>

      {isOtherCategory ? (
        <Box flexGrow={1}>
          <SignUpNames
            label={intakeLabels?.partnerSubCategoryOther ?? ''}
            required
            value={otherText}
            error={showErrors && (otherText === '' || otherText.length >= 100)}
            id={'partnerSubCategoryOther'}
            onChange={handleOtherCategoryChange}
          />
          {otherText.length >= 100 && <CharacterLimitHelperText limit={100} />}
        </Box>
      ) : (
        <></>
      )}
    </RowWrap>
  );
}

export default PartnerCategoryInput;
